import { useState, useMemo } from "react";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  MoreHorizontal,
  Trash2,
  Users,
  Filter,
  CalendarDays,
} from "lucide-react";
import {
  ProjectAvailability,
  AvailabilityStatus,
} from "@/hooks/useProjectPeople";
import {
  AvailabilityStatusBadge,
  availabilityStatuses,
  getStatusColor,
} from "./AvailabilityStatusBadge";
import { PersonHoverCard } from "@/components/people/PersonHoverCard";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";

// Default filters: all statuses except "not_available" (shown in separate section)
const defaultStatusFilters: AvailabilityStatus[] = availabilityStatuses.filter(
  (s) => s !== "not_available",
);

interface AvailabilityTableProps {
  availability: ProjectAvailability[];
  projectDays: Date[];
  eventDays: Date[];
  planningDays: Date[];
  onUpdateStatus: (
    personId: string,
    status: AvailabilityStatus,
    days: string[],
  ) => void;
  onBatchUpdate: (
    personIds: string[],
    status: AvailabilityStatus,
    days: string[],
  ) => void;
  onRemove: (id: string) => void;
  onUpdateCrewListRemark: (availabilityId: string, remark: string) => void;
  isUpdating?: boolean;
}

export function AvailabilityTable({
  availability,
  projectDays,
  eventDays,
  planningDays,
  onUpdateStatus,
  onBatchUpdate,
  onRemove,
  onUpdateCrewListRemark,
  isUpdating,
}: AvailabilityTableProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [batchStatus, setBatchStatus] =
    useState<AvailabilityStatus>("available");
  const [statusFilters, setStatusFilters] =
    useState<AvailabilityStatus[]>(defaultStatusFilters);

  // Filter availability by selected statuses
  const filteredAvailability = useMemo(
    () => availability.filter((a) => statusFilters.includes(a.status)),
    [availability, statusFilters],
  );

  const unavailablePeople = useMemo(
    () => availability.filter((a) => a.status === "not_available"),
    [availability],
  );

  const toggleStatusFilter = (status: AvailabilityStatus) => {
    setStatusFilters((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
  };

  const toggleSelection = (personId: string) => {
    setSelectedIds((prev) =>
      prev.includes(personId)
        ? prev.filter((id) => id !== personId)
        : [...prev, personId],
    );
  };

  const toggleAll = () => {
    if (selectedIds.length === filteredAvailability.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredAvailability.map((a) => a.person_id));
    }
  };

  const handleBatchUpdate = () => {
    if (selectedIds.length > 0) {
      const allDays = projectDays.map((d) => format(d, "yyyy-MM-dd"));
      onBatchUpdate(selectedIds, batchStatus, allDays);
      setSelectedIds([]);
    }
  };

  const planningDayStrings = planningDays.map((day) =>
    format(day, "yyyy-MM-dd"),
  );
  const eventDayStrings = eventDays.map((day) => format(day, "yyyy-MM-dd"));

  const getDayStatus = (
    personAvailability: ProjectAvailability,
    day: Date,
  ): boolean => {
    const dayStr = format(day, "yyyy-MM-dd");
    return personAvailability.available_days?.includes(dayStr) ?? false;
  };

  const toggleDay = (personAvailability: ProjectAvailability, day: Date) => {
    const dayStr = format(day, "yyyy-MM-dd");
    const currentDays = personAvailability.available_days || [];
    const newDays = currentDays.includes(dayStr)
      ? currentDays.filter((d) => d !== dayStr)
      : [...currentDays, dayStr];
    onUpdateStatus(
      personAvailability.person_id,
      personAvailability.status,
      newDays,
    );
  };

  const getPersonName = (personAvailability: ProjectAvailability) =>
    personAvailability.person
      ? `${personAvailability.person.first_name} ${personAvailability.person.last_name}`
      : "Unknown";

  if (availability.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No people added to this project yet.</p>
        <p className="text-sm">Click "Add Person" to get started.</p>
      </div>
    );
  }

  const activeFilterCount = statusFilters.length;
  const isFilterModified =
    statusFilters.length !== defaultStatusFilters.length ||
    !defaultStatusFilters.every((s) => statusFilters.includes(s));

  return (
    <div className="space-y-4">
      {/* Status filters */}
      <div className="flex items-center gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <Filter className="h-4 w-4" />
              Filter by Status
              {isFilterModified && (
                <span className="ml-1 rounded-full bg-primary text-primary-foreground px-1.5 py-0.5 text-xs">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56" align="start">
            <div className="space-y-3">
              <Label className="text-sm font-medium">Show statuses</Label>
              <div className="space-y-2">
                {availabilityStatuses
                  .filter((s) => s !== "not_available")
                  .map((status) => (
                    <div key={status} className="flex items-center gap-2">
                      <Checkbox
                        id={`filter-${status}`}
                        checked={statusFilters.includes(status)}
                        onCheckedChange={() => toggleStatusFilter(status)}
                      />
                      <label
                        htmlFor={`filter-${status}`}
                        className="flex-1 cursor-pointer"
                      >
                        <AvailabilityStatusBadge status={status} />
                      </label>
                    </div>
                  ))}
              </div>
              {isFilterModified && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => setStatusFilters(defaultStatusFilters)}
                >
                  Reset to Default
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>
        <span className="text-sm text-muted-foreground">
          Showing {filteredAvailability.length} of {availability.length} people
        </span>
      </div>

      {/* Batch actions */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
          <span className="text-sm font-medium">
            {selectedIds.length} selected
          </span>
          <Select
            value={batchStatus}
            onValueChange={(v) => setBatchStatus(v as AvailabilityStatus)}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availabilityStatuses.map((s) => (
                <SelectItem key={s} value={s}>
                  <AvailabilityStatusBadge status={s} showIcon={false} />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={handleBatchUpdate} disabled={isUpdating}>
            Apply to Selected
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}>
            Clear
          </Button>
        </div>
      )}

      <div className="relative w-full min-w-0 max-w-full overflow-x-auto">
        <table className="min-w-max caption-bottom text-sm">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={
                    selectedIds.length === filteredAvailability.length &&
                    filteredAvailability.length > 0
                  }
                  onCheckedChange={toggleAll}
                />
              </TableHead>
              <TableHead className="min-w-[180px]">Person</TableHead>
              <TableHead className="min-w-[120px]">Status</TableHead>
              <TableHead className="min-w-[150px]">Crew List Remark</TableHead>
              {projectDays.map((day) => (
                <TableHead
                  key={day.toISOString()}
                  className="text-center min-w-[60px]"
                >
                  <div className="text-xs">
                    <div>{format(day, "EEE")}</div>
                    <div className="font-normal">{format(day, "dd.MM")}</div>
                  </div>
                </TableHead>
              ))}
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAvailability.map((avail) => (
              <TableRow
                key={avail.id}
                className={cn(
                  selectedIds.includes(avail.person_id) && "bg-muted/50",
                )}
              >
                <TableCell>
                  <Checkbox
                    checked={selectedIds.includes(avail.person_id)}
                    onCheckedChange={() => toggleSelection(avail.person_id)}
                    aria-label={`Select ${getPersonName(avail)}`}
                  />
                </TableCell>
                <TableCell>
                  {avail.person ? (
                    <PersonHoverCard
                      personId={avail.person.id}
                      firstName={avail.person.first_name}
                      lastName={avail.person.last_name}
                      personType={avail.person.person_type}
                      email={avail.person.email}
                      phone={avail.person.phone}
                    >
                      <p className="font-medium">
                        {avail.person.first_name} {avail.person.last_name}
                      </p>
                    </PersonHoverCard>
                  ) : (
                    <p className="font-medium">Unknown</p>
                  )}
                </TableCell>
                <TableCell>
                  <Select
                    value={avail.status}
                    onValueChange={(v) =>
                      onUpdateStatus(
                        avail.person_id,
                        v as AvailabilityStatus,
                        avail.available_days || [],
                      )
                    }
                  >
                    <SelectTrigger
                      className="w-[130px]"
                      aria-label={`Availability status for ${getPersonName(avail)}`}
                    >
                      <SelectValue>
                        <AvailabilityStatusBadge
                          status={avail.status}
                          showIcon={false}
                        />
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {availabilityStatuses.map((s) => (
                        <SelectItem key={s} value={s}>
                          <AvailabilityStatusBadge status={s} />
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Input
                    className="h-7 text-xs"
                    defaultValue={avail.crew_list_remark ?? ""}
                    placeholder="Remark..."
                    aria-label={`Crew list remark for ${getPersonName(avail)}`}
                    onBlur={(e) =>
                      onUpdateCrewListRemark(avail.id, e.target.value)
                    }
                  />
                </TableCell>
                {projectDays.map((day) => {
                  const dayLabel = format(day, "yyyy-MM-dd");
                  const isAvailable = getDayStatus(avail, day);
                  return (
                    <TableCell
                      key={day.toISOString()}
                      className={cn(
                        "text-center p-1 cursor-pointer transition-colors",
                        isAvailable
                          ? getStatusColor(avail.status)
                          : "hover:bg-muted/50",
                      )}
                      onClick={() => toggleDay(avail, day)}
                    >
                      <Checkbox
                        checked={isAvailable}
                        onCheckedChange={() => toggleDay(avail, day)}
                        className="mx-auto"
                        aria-label={`Availability ${getPersonName(avail)} ${dayLabel}`}
                      />
                    </TableCell>
                  );
                })}
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={`Availability actions for ${getPersonName(avail)}`}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() =>
                          onUpdateStatus(
                            avail.person_id,
                            avail.status,
                            eventDayStrings,
                          )
                        }
                      >
                        <CalendarDays className="h-4 w-4 mr-2" />
                        Set event dates
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          onUpdateStatus(
                            avail.person_id,
                            avail.status,
                            planningDayStrings,
                          )
                        }
                      >
                        <CalendarDays className="h-4 w-4 mr-2" />
                        Set planning dates
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => onRemove(avail.id)}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </table>
      </div>

      {unavailablePeople.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">
            Unavailable ({unavailablePeople.length})
          </h4>
          <Table>
            <TableBody>
              {unavailablePeople.map((avail) => (
                <TableRow key={avail.id} className="opacity-50">
                  <TableCell>
                    {avail.person ? (
                      <PersonHoverCard
                        personId={avail.person.id}
                        firstName={avail.person.first_name}
                        lastName={avail.person.last_name}
                        personType={avail.person.person_type}
                        email={avail.person.email}
                        phone={avail.person.phone}
                      >
                        <p className="font-medium">
                          {avail.person.first_name} {avail.person.last_name}
                        </p>
                      </PersonHoverCard>
                    ) : (
                      <p className="font-medium">Unknown</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={avail.status}
                      onValueChange={(v) =>
                        onUpdateStatus(
                          avail.person_id,
                          v as AvailabilityStatus,
                          avail.available_days || [],
                        )
                      }
                    >
                      <SelectTrigger className="w-[130px]">
                        <SelectValue>
                          <AvailabilityStatusBadge
                            status={avail.status}
                            showIcon={false}
                          />
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {availabilityStatuses.map((s) => (
                          <SelectItem key={s} value={s}>
                            <AvailabilityStatusBadge status={s} />
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input
                      className="h-7 text-xs"
                      defaultValue={avail.crew_list_remark ?? ""}
                      placeholder="Remark..."
                      onBlur={(e) =>
                        onUpdateCrewListRemark(avail.id, e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => onRemove(avail.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
